// Progress arithmetic for a background S3 synchronisation.
//
// Separate from the component so the awkward cases — a job whose totals are
// not known yet, one that skipped objects, one reporting more done than
// announced — can be tested without a DOM.
import type { SyncResult } from '../api/types';

/**
 * Share of the job that is finished, 0..1.
 *
 * Bytes are preferred over object counts: a bucket of one 2 GB file and four
 * small ones is 20% done by count after the small ones, which would leave the
 * bar apparently stuck for the entire real transfer.
 *
 * Skipped objects count as finished. They are not coming back in this run, and
 * a bar that can never reach its end reads as a hang.
 */
export function syncFraction(job: SyncResult): number {
  const doneBytes = (job.bytes ?? 0) + (job.skippedBytes ?? 0);
  if (job.totalBytes > 0) return clamp(doneBytes / job.totalBytes);
  const doneObjects = (job.imported ?? 0) + (job.skipped ?? 0);
  if (job.totalObjects > 0) return clamp(doneObjects / job.totalObjects);
  // Totals unknown: the source listing has not finished yet.
  return 0;
}

function clamp(n: number): number {
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(1, n);
}
